import { useState, useEffect, useRef, useCallback } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import { BottomTabBar } from '@/components/layout/BottomTabBar'
import { apiUrl } from '@/lib/api'

interface TokenData {
  token: string
  liffUrl: string
  expiresAt: string
}

interface TokenStatus {
  status: 'pending' | 'completed' | 'expired'
  submittedAt?: string
  missingItems?: string[]
  totalItems?: number
  checkedItems?: number
}

export function OrientationQRPage() {
  const { eventId, studentId } = useParams()
  const navigate = useNavigate()
  const [tokenData, setTokenData] = useState<TokenData | null>(null)
  const [status, setStatus] = useState<TokenStatus | null>(null)
  const [timeLeft, setTimeLeft] = useState('')
  const [isLoading, setIsLoading] = useState(true)
  const [toast, setToast] = useState<string | null>(null)
  const pollRef = useRef<ReturnType<typeof setInterval> | null>(null)
  const timerRef = useRef<ReturnType<typeof setInterval> | null>(null)

  function showToast(msg: string) {
    setToast(msg)
    setTimeout(() => setToast(null), 2000)
  }

  // Create token
  useEffect(() => {
    if (!eventId || !studentId) return

    async function createToken() {
      try {
        const res = await fetch(apiUrl('/api/orientations/tokens'), {
          method: 'POST',
          credentials: 'include',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ studentId, eventId }),
        })
        const data = await res.json()
        if (data.success) {
          setTokenData(data.data)
        } else {
          showToast(data.error?.messageTh || 'เกิดข้อผิดพลาด')
        }
      } catch {
        showToast('เกิดข้อผิดพลาด')
      } finally {
        setIsLoading(false)
      }
    }

    createToken()
  }, [eventId, studentId])

  // Countdown timer
  useEffect(() => {
    if (!tokenData) return

    function updateTimer() {
      const expiresAt = new Date(tokenData!.expiresAt).getTime()
      const now = Date.now()
      const diff = expiresAt - now

      if (diff <= 0) {
        setTimeLeft('หมดอายุ')
        if (timerRef.current) clearInterval(timerRef.current)
        if (pollRef.current) clearInterval(pollRef.current)
        return
      }

      const mins = Math.floor(diff / 60000)
      const secs = Math.floor((diff % 60000) / 1000)
      setTimeLeft(`${mins}:${String(secs).padStart(2, '0')}`)
    }

    updateTimer()
    timerRef.current = setInterval(updateTimer, 1000)

    return () => {
      if (timerRef.current) clearInterval(timerRef.current)
    }
  }, [tokenData])

  const stopPolling = useCallback(() => {
    if (pollRef.current) {
      clearInterval(pollRef.current)
      pollRef.current = null
    }
  }, [])

  // Poll status
  useEffect(() => {
    if (!tokenData) return

    async function checkStatus() {
      try {
        const res = await fetch(apiUrl(`/api/orientations/tokens/${tokenData!.token}/status`), {
          credentials: 'include',
        })
        const data = await res.json()
        if (data.success) {
          setStatus(data.data)
          if (data.data.status === 'completed' || data.data.status === 'expired') {
            stopPolling()
          }
        }
      } catch {
        // silent
      }
    }

    checkStatus()
    pollRef.current = setInterval(checkStatus, 5000)

    return () => {
      stopPolling()
    }
  }, [tokenData, stopPolling])

  if (isLoading) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-gray-50">
        <div className="text-gray-400">กำลังสร้างลิงก์...</div>
      </div>
    )
  }

  const isCompleted = status?.status === 'completed'
  const isExpired = status?.status === 'expired' || timeLeft === 'หมดอายุ'

  return (
    <div className="min-h-screen bg-gray-50 pb-20">
      <header className="bg-white px-4 py-3 shadow-sm">
        <div className="mx-auto flex max-w-lg items-center gap-3">
          <button onClick={() => navigate(`/orientation/${eventId}`)} className="text-gray-600">
            ⟨
          </button>
          <h1 className="text-lg font-bold text-gray-900">QR สำหรับผู้ปกครอง</h1>
        </div>
      </header>

      <main className="mx-auto max-w-lg px-4 py-4">
        {isCompleted ? (
          /* Success state */
          <div className="rounded-lg bg-white p-6 text-center shadow-sm">
            <div className="mx-auto mb-3 flex h-16 w-16 items-center justify-center rounded-full bg-green-100">
              <span className="text-3xl">✓</span>
            </div>
            <h2 className="text-lg font-bold text-gray-900">บันทึกเรียบร้อย!</h2>
            {status.totalItems != null && status.checkedItems != null && (
              <p className="mt-2 text-sm text-gray-600">
                ตรวจสอบ {status.checkedItems}/{status.totalItems} รายการ
              </p>
            )}
            {status.missingItems && status.missingItems.length > 0 && (
              <div className="mt-3 rounded-lg bg-amber-50 p-3 text-left">
                <p className="mb-1 text-sm font-medium text-amber-700">ของที่ขาด:</p>
                <ul className="list-inside list-disc text-sm text-amber-600">
                  {status.missingItems.map((item) => (
                    <li key={item}>{item}</li>
                  ))}
                </ul>
              </div>
            )}
            <button
              onClick={() => navigate(`/orientation/${eventId}`)}
              className="mt-4 w-full rounded-lg bg-primary-600 py-3 text-sm font-semibold text-white"
            >
              ถัดไป
            </button>
          </div>
        ) : (
          /* QR / Link display */
          <div className="rounded-lg bg-white p-6 text-center shadow-sm">
            {isExpired ? (
              <div>
                <div className="mx-auto mb-3 flex h-16 w-16 items-center justify-center rounded-full bg-red-100">
                  <span className="text-3xl">⏰</span>
                </div>
                <h2 className="text-lg font-bold text-gray-900">ลิงก์หมดอายุ</h2>
                <p className="mt-2 text-sm text-gray-500">กรุณาสร้างลิงก์ใหม่</p>
                <button
                  onClick={() => window.location.reload()}
                  className="mt-4 w-full rounded-lg bg-primary-600 py-3 text-sm font-semibold text-white"
                >
                  สร้างลิงก์ใหม่
                </button>
              </div>
            ) : tokenData ? (
              <div>
                <p className="mb-4 text-sm text-gray-500">ให้ผู้ปกครองสแกน</p>

                {/* Show URL as large selectable text */}
                <div className="mb-4 rounded-lg bg-gray-50 p-4">
                  <a
                    href={tokenData.liffUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="break-all text-sm font-medium text-primary-600 underline"
                  >
                    {tokenData.liffUrl}
                  </a>
                </div>

                {/* Timer */}
                <div className="mb-4 flex items-center justify-center gap-2">
                  <span className="text-sm text-gray-500">หมดอายุใน</span>
                  <span
                    className={`rounded-full px-3 py-1 text-sm font-semibold ${
                      timeLeft <= '5:00'
                        ? 'bg-red-100 text-red-700'
                        : 'bg-gray-100 text-gray-700'
                    }`}
                  >
                    {timeLeft}
                  </span>
                </div>

                {/* Status indicator */}
                <div className="flex items-center justify-center gap-2 text-sm text-gray-400">
                  <span className="inline-block h-2 w-2 animate-pulse rounded-full bg-amber-400" />
                  รอผู้ปกครองเปิดลิงก์...
                </div>
              </div>
            ) : (
              <p className="text-sm text-gray-400">ไม่สามารถสร้างลิงก์ได้</p>
            )}
          </div>
        )}
      </main>

      {toast && (
        <div className="fixed left-1/2 top-4 z-50 -translate-x-1/2 rounded-lg bg-gray-800 px-4 py-2 text-sm text-white shadow-lg">
          {toast}
        </div>
      )}

      <BottomTabBar active="more" />
    </div>
  )
}
